/**
 * Barra de chips de instrumentos (M6 gráfico) — seleciona o instrumento exibido.
 * Corpo do chip reflete o nível/seleção; a **faixa esquerda (3px)** traz a cor de
 * identidade da barragem (fiel ao V8). Clique chama `onSelect(id)` (seleção única).
 */
import { LevelIcon } from '@/ui';
import { NIVEL_EXIBIDO_UI } from '@/ui/tokens';
import { ehNivelMedido, SEM_LEITURA, type NivelExibido } from '@/domain/instrumentos/nivel-exibido';
import type { Instrument } from '@/domain';

export interface InstrumentChipBarProps {
  instruments: Instrument[];
  levelById: Record<string, NivelExibido>;
  /** Cor de identidade da barragem por `damId` (faixa esquerda do chip). */
  damColorById?: Record<string, string>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export default function InstrumentChipBar({
  instruments, levelById, damColorById = {}, selectedId, onSelect,
}: InstrumentChipBarProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {instruments.map((inst) => {
        // GUPM-228 — sem entrada no mapa de niveis = sem leitura: chip neutro,
        // nao verde.
        const level = levelById[inst.id] ?? SEM_LEITURA;
        const ui = NIVEL_EXIBIDO_UI[level];
        const selected = inst.id === selectedId;
        // Só nível MEDIDO acima de NORMAL merece destaque/ícone de severidade:
        // estado sem lastro (sem leitura, sem faixa) não é anomalia (GUPM-247).
        const nonNormal = ehNivelMedido(level) && level !== 'NORMAL';
        const dam = damColorById[inst.damId] ?? '#94a3b8';
        const edge = selected || nonNormal ? ui.border : '#28334A';
        return (
          <button
            key={inst.id}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(inst.id)}
            className="px-3 py-1 rounded-lg text-[11px] font-bold border transition"
            style={{
              // Só longhands (sem o atalho borderColor) — evita o aviso de mistura
              // shorthand/longhand do React e deixa o jsdom expor borderLeftColor.
              borderTopColor: edge,
              borderRightColor: edge,
              borderBottomColor: edge,
              // Faixa esquerda = cor de identidade da barragem (3px).
              borderLeftColor: dam,
              borderLeftWidth: 3,
              borderLeftStyle: 'solid',
              background: selected ? ui.bg : 'transparent',
              color: selected || nonNormal ? ui.color : '#94a3b8',
            }}
          >
            {inst.code}
            {nonNormal && <LevelIcon level={level} size={11} className="ml-0.5 align-middle" />}
          </button>
        );
      })}
    </div>
  );
}
